'use client';

import type { KeyVal } from '@dealerportal/common';
import {
  convertSpecValue,
  getSpecTranslationKey,
  translateOrFallback,
  useLocale,
  useTranslations,
} from '@dealerportal/i18n/client';
import {
  remapBrandName,
  remapProductName,
  toKebab,
} from '@dealerportal/utilities';
import classNames from 'classnames';

type Props = {
  specs: KeyVal[];
};

export const ProductSpec = ({ specs }: Props) => {
  const spaceNamespace = 'specs';
  const t = useTranslations('products');
  const tSpecs = useTranslations(spaceNamespace);
  const locale = useLocale() as 'en' | 'pt' | 'es';
  specs = specs.filter(({ value }) => value);

  const translateSpecKey = (key: string): string =>
    translateOrFallback(
      tSpecs,
      spaceNamespace,
      getSpecTranslationKey(key),
      key
    );

  // `valueParts` carries the per-value labels behind `value`; each may name a
  // `specs` key that overrides the vendor label for the active locale.
  const translateSpecValue = ({ value, valueParts }: KeyVal): string => {
    if (!valueParts?.length) return value;
    return valueParts
      .map(({ label, messageKey }) =>
        messageKey
          ? translateOrFallback(tSpecs, spaceNamespace, messageKey, label)
          : label
      )
      .join(', ');
  };

  return (
    <div
      data-cy="spec-table"
      className="ui-mt-6 ui-w-full ui-border-collapse! ui-border-spacing-0!"
    >
      {specs
        .filter(({ value }) => value && value !== 'false' && value !== 'No')
        .map((spec, index) => {
          let remappedValue = translateSpecValue(spec);

          if (spec.key === 'Brand') {
            remappedValue = remapBrandName(spec.value);
          } else if (spec.key === 'Product Line') {
            remappedValue = remapProductName(spec.value);
          }

          // Convert units based on locale
          remappedValue = convertSpecValue(remappedValue, spec.key, locale);

          const dataAttr = {
            [`data-${toKebab(spec.key, true)}`]: remappedValue,
          };
          return (
            <div
              {...dataAttr}
              key={`${spec.key}-${remappedValue}`}
              className={classNames(
                'ui-h-12 ui-flex ui-border-solid ui-border-0 ui-border-b ui-border-gray-300',
                { 'ui-border-transparent': specs.length - 1 === index }
              )}
            >
              <div className="ui-max-w-full ui-w-80 ui-px-2 ui-flex ui-items-center ui-text-gray-500 ui-text-sm md:ui-text-base">
                {translateSpecKey(spec.key)}
              </div>

              <div className="ui-bg-white ui-w-full ui-pl-4 ui-flex ui-items-center ui-font-semibold ui-text-sm md:ui-text-base ui-border-solid ui-border-0 ui-border-l ui-border-gray-300">
                {remappedValue === 'true' ? t('yes') : remappedValue}
              </div>
            </div>
          );
        })}
    </div>
  );
};
